/**
 * Conta → Uso: this-month standing vs the account plan, plus Coletor / Rotina
 * charts (formerly Início `/usage`).
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis } from 'recharts';
import {
  BASE_TEAM_SEATS,
  FREE_INCLUDED_SEATS,
  INBOUND_FREE_MONTHLY,
  INBOUND_PACK_VOLUME,
  PORTAL_PATHS,
  SEAT_PACK_INCLUDED_INBOUND,
  resolveSeatCount,
} from '@x9/billing';
import PackUpgradeModal, { type PackFocus } from './PackUpgradeModal';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from './ui/chart';
import { Progress } from './ui/progress';
import { Skeleton } from './ui/skeleton';
import { getFirebaseAuth, getFirebaseDb } from '../config/firebase';
import { useBillingSummary } from '../hooks/useBillingSummary';
import { useUsageLedger, type Counters } from '../hooks/useUsageLedger';

export type UsageCheckout = (opts: {
  interval: 'MONTHLY' | 'YEARLY';
  seatCount: number;
  volumePacks: number;
  mode?: 'pack-upgrade';
}) => void;

type Props = {
  onNavigate: (path: string) => void;
  onUpgrade: () => void;
  onCheckout: UsageCheckout;
};

type ActiveSub = {
  status?: string;
  billingInterval?: string;
  seatCount?: number;
  seatPacks?: number;
  volumePacks?: number;
  renewalDueDate?: string;
  nextDueDate?: string;
};

type FreeLimits = {
  inboundSubmissionsMonth?: number | null;
  executorSeatsMonth?: number | null;
  bucketCap?: number | null;
};

const collectorChartConfig = {
  inboundAccepted: { label: 'Contribuições aceitas', color: 'var(--chart-2)' },
  inboundReceived: { label: 'Contribuições recebidas', color: 'var(--chart-5)' },
  bucketsCollector: { label: 'Pastas coletor (criadas)', color: 'var(--chart-3)' },
} satisfies ChartConfig;

const routineChartConfig = {
  bucketsRoutine: { label: 'Rotinas criadas', color: 'var(--chart-5)' },
  runsSelf: { label: 'Execuções para você', color: 'var(--chart-2)' },
  runsOthers: { label: 'Execuções para outros', color: 'var(--chart-4)' },
} satisfies ChartConfig;

function monthKeyFromDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function parseMonthKey(mk: string): Date | null {
  const [y, m] = mk.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
}

function lastSixMonthKeys(endKey?: string): string[] {
  const end = (endKey && parseMonthKey(endKey)) || new Date();
  const keys: string[] = [];
  for (let i = 5; i >= 0; i -= 1) {
    keys.push(monthKeyFromDate(new Date(end.getFullYear(), end.getMonth() - i, 1)));
  }
  return keys;
}

function monthShort(mk: string): string {
  const [, m] = mk.split('-');
  const names = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? names[idx] : mk;
}

function monthLabel(mk?: string): string {
  if (!mk) return '';
  const [y, m] = mk.split('-');
  const names = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${names[idx]} de ${y}` : mk;
}

function countOf(counters: Counters | undefined, key: string): number {
  return Number(counters?.[key]) || 0;
}

function collectorBuckets(counters: Counters | undefined): number {
  return Math.max(0, countOf(counters, 'bucketsCreated') - countOf(counters, 'bucketsCreatedRoutine'));
}

function activeSubscription(
  summary: ReturnType<typeof useBillingSummary>['summary'],
): ActiveSub | null {
  const activeStatuses = ['ACTIVE', 'CANCEL_AT_PERIOD_END', 'PAST_DUE', 'GRACE_PERIOD'];
  const activeSubs = ((summary?.subscriptions || []) as ActiveSub[]).filter((s) =>
    activeStatuses.includes(String(s.status || '')),
  );
  return (
    activeSubs.find((s) => String(s.billingInterval || '').toUpperCase() === 'YEARLY')
    || activeSubs[0]
    || null
  );
}

function UsageMeter({
  label,
  used,
  limit,
  lifetime,
  exhaustedLabel = 'Você chegou no limite deste ciclo',
}: {
  label: string;
  used: number;
  /** `null` = unlimited on this plan. */
  limit: number | null;
  lifetime: number;
  exhaustedLabel?: string;
}) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(limit, 1)) * 100);
  const over = !unlimited && used >= limit;
  const remaining = unlimited ? 0 : Math.max(0, limit - used);
  const status = unlimited
    ? 'Ilimitado no seu plano'
    : over
      ? exhaustedLabel
      : `${remaining} restante${remaining === 1 ? '' : 's'} neste mês`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between gap-2 space-y-0 p-2.5 pb-1 sm:p-4 sm:pb-2">
        <CardDescription className="truncate text-xs sm:text-sm">{label}</CardDescription>
        <CardTitle className="text-sm sm:text-base">{unlimited ? used : `${used} / ${limit}`}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1 p-2.5 pt-0 sm:gap-2 sm:p-4 sm:pt-0">
        <Progress value={pct} className="h-1.5 sm:h-2" />
        <CardDescription className="text-[11px] sm:text-sm">
          {status}
          {lifetime > 0 ? ` · ${lifetime} no total` : ''}
        </CardDescription>
      </CardContent>
    </Card>
  );
}

function UsageTrendChart({
  config,
  data,
  reference = null,
}: {
  config: ChartConfig;
  data: Array<Record<string, string | number>>;
  reference?: { value: number; label: string } | null;
}) {
  return (
    <ChartContainer
      config={config}
      className="aspect-auto h-[140px] w-full touch-pan-y sm:h-[160px]"
    >
      <LineChart
        accessibilityLayer
        data={data}
        margin={{ left: 8, right: 8, top: 4 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
        />
        <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
        <ChartLegend content={<ChartLegendContent />} />
        {reference != null && reference.value > 0 ? (
          <ReferenceLine
            y={reference.value}
            stroke="var(--muted-foreground)"
            strokeDasharray="3 3"
            label={reference.label}
          />
        ) : null}
        {Object.keys(config).map((key) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={`var(--color-${key})`}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}

function UsageDashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-4 w-72" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-baseline justify-between gap-2 space-y-0 p-4 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-12" />
            </CardHeader>
            <CardContent className="flex flex-col gap-2 p-4 pt-0">
              <Skeleton className="h-2 w-full" />
              <Skeleton className="h-4 w-36" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {Array.from({ length: 2 }, (_, i) => (
          <Card key={i}>
            <CardHeader className="p-4 pb-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-full" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Skeleton className="h-[160px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function UsageDashboard({ onNavigate, onUpgrade, onCheckout }: Props) {
  const { root, months, ready } = useUsageLedger(true);
  const { summary, invalidate, refresh } = useBillingSummary(true);
  const [packFocus, setPackFocus] = useState<PackFocus | null>(null);
  const [limits, setLimits] = useState<FreeLimits | null>(null);
  const [seatsOccupied, setSeatsOccupied] = useState(0);

  useEffect(() => {
    const uid = getFirebaseAuth().currentUser?.uid;
    if (!uid) return;
    const db = getFirebaseDb();
    const unsubUser = onSnapshot(doc(db, 'users', uid), (snap) => {
      const raw = snap.data()?.freeLimitsSnapshot;
      setLimits(raw && typeof raw === 'object' ? (raw as FreeLimits) : null);
    });
    const unsubSeats = onSnapshot(doc(db, 'seatPlans', uid), (snap) => {
      const slots = (snap.data()?.slots || []) as Array<{ occupant?: unknown }>;
      setSeatsOccupied(slots.filter((s) => s.occupant).length);
    });
    return () => {
      unsubUser();
      unsubSeats();
    };
  }, []);

  const current = root?.currentMonthCounters || {};
  const lifetime = root?.lifetime || {};
  const hasPaid = summary?.hasPaidAccess === true;
  const sub = activeSubscription(summary);

  const seatCount = hasPaid ? resolveSeatCount(sub) : 0;
  const volumePacks = Number.isFinite(Number(sub?.volumePacks)) ? Number(sub?.volumePacks) : 0;
  const inboundAllowance = hasPaid
    ? INBOUND_FREE_MONTHLY
      + (seatCount > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
      + volumePacks * INBOUND_PACK_VOLUME
    : (limits?.inboundSubmissionsMonth === null
      ? null
      : (typeof limits?.inboundSubmissionsMonth === 'number'
        ? limits.inboundSubmissionsMonth
        : INBOUND_FREE_MONTHLY));
  const seatAllowance = hasPaid
    ? Math.max(seatCount || BASE_TEAM_SEATS, BASE_TEAM_SEATS)
    : (limits?.executorSeatsMonth === null
      ? null
      : (typeof limits?.executorSeatsMonth === 'number'
        ? limits.executorSeatsMonth
        : FREE_INCLUDED_SEATS));

  const chartMonths = useMemo(() => {
    const byKey = new Map((months || []).map((m) => [m.monthKey, m.counters || {}]));
    return lastSixMonthKeys(root?.currentMonthKey).map((monthKey) => ({
      monthKey,
      counters: byKey.get(monthKey) || {},
    }));
  }, [months, root]);

  const collectorData = useMemo(
    () => chartMonths.map((m) => ({
      month: monthShort(m.monthKey),
      inboundAccepted: countOf(m.counters, 'inboundAccepted'),
      inboundReceived: countOf(m.counters, 'inboundReceived'),
      bucketsCollector: collectorBuckets(m.counters),
    })),
    [chartMonths],
  );

  const routineData = useMemo(
    () => chartMonths.map((m) => ({
      month: monthShort(m.monthKey),
      bucketsRoutine: countOf(m.counters, 'bucketsCreatedRoutine'),
      runsSelf: countOf(m.counters, 'runsDispatchedSelf'),
      runsOthers: countOf(m.counters, 'runsDispatchedToOthers'),
    })),
    [chartMonths],
  );

  const held = Number(current.inboundHeld) || 0;
  const openPack = (focus: PackFocus) => setPackFocus(focus);

  if (!ready) {
    return <UsageDashboardSkeleton />;
  }

  return (
    <div className="flex min-h-0 flex-col gap-3 sm:gap-4">
      <CardDescription>
        {root?.currentMonthKey
          ? `Onde você está em ${monthLabel(root.currentMonthKey)}, no ${hasPaid ? 'plano Equipe' : 'plano gratuito'}.`
          : `Onde você está no ${hasPaid ? 'plano Equipe' : 'plano gratuito'}.`}
      </CardDescription>

      {held > 0 && (
        <Alert className="flex flex-col gap-3">
          <AlertTitle>
            {held} contribuiç{held > 1 ? 'ões' : 'ão'} aguardando
          </AlertTitle>
          <AlertDescription>
            Sua conta recebeu mais contribuições do que o volume mensal do plano. Nada foi
            perdido: elas estão guardadas e são liberadas ao ampliar o plano ou no próximo ciclo.
          </AlertDescription>
          <div>
            <Button width="auto" size="sm" onClick={() => openPack('volume')}>
              Ampliar volume
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-5">
        <UsageMeter
          label="Tarefas criadas"
          used={countOf(current, 'tasksCreated')}
          limit={null}
          lifetime={countOf(lifetime, 'tasksCreated')}
        />
        <UsageMeter
          label="Pastas coletor"
          used={collectorBuckets(current)}
          limit={limits?.bucketCap ?? null}
          lifetime={collectorBuckets(lifetime)}
        />
        <UsageMeter
          label="Rotinas"
          used={countOf(current, 'bucketsCreatedRoutine')}
          limit={limits?.bucketCap ?? null}
          lifetime={countOf(lifetime, 'bucketsCreatedRoutine')}
        />
        <UsageMeter
          label="Contribuições aceitas"
          used={countOf(current, 'inboundAccepted')}
          limit={inboundAllowance}
          lifetime={countOf(lifetime, 'inboundAccepted')}
        />
        <UsageMeter
          label="Assentos da equipe"
          used={seatsOccupied}
          limit={seatAllowance}
          lifetime={0}
          exhaustedLabel="Todos os assentos estão ocupados"
        />
      </div>

      <div className="grid gap-2 sm:gap-3 lg:grid-cols-2">
        <Card>
        <CardHeader className="p-2.5 pb-1 sm:p-4 sm:pb-2">
          <CardTitle>Coletor</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Contribuições de outras pessoas e pastas coletor criadas.
              {inboundAllowance != null ? ` Limite atual: ${inboundAllowance}/mês.` : ' Sem limite de volume.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-2.5 pt-0 sm:p-4 sm:pt-0">
            <UsageTrendChart
              config={collectorChartConfig}
              data={collectorData}
              reference={inboundAllowance != null
                ? { value: inboundAllowance, label: `limite ${inboundAllowance}` }
                : null}
            />
          </CardContent>
          <CardFooter className="justify-start p-2.5 pt-0 sm:p-4 sm:pt-0">
            <Button width="auto" size="sm" className="w-auto" onClick={() => openPack('volume')}>
              Aumentar volume
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="p-2.5 pb-1 sm:p-4 sm:pb-2">
            <CardTitle>Rotina</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {hasPaid
                ? `Rotinas criadas e execuções despachadas · ${seatCount || BASE_TEAM_SEATS} assentos.`
                : 'Rotinas criadas e execuções despachadas · assentos no plano Equipe.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-2.5 pt-0 sm:p-4 sm:pt-0">
            <UsageTrendChart config={routineChartConfig} data={routineData} />
          </CardContent>
          <CardFooter className="justify-start p-2.5 pt-0 sm:p-4 sm:pt-0">
            <Button width="auto" size="sm" className="w-auto" onClick={() => openPack('seats')}>
              {hasPaid ? 'Ajustar assentos' : 'Conhecer plano Equipe'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-2.5 pb-1 sm:p-4 sm:pb-2">
          <CardTitle>{hasPaid ? 'Plano Equipe ativo' : 'Plano gratuito'}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {hasPaid
              ? 'Gerencie equipe, assinatura e pagamentos nas outras abas.'
              : `Uso pessoal ilimitado. O plano Equipe traz ${BASE_TEAM_SEATS} assentos nominais a partir de R$ 35/mês.`}
          </CardDescription>
        </CardHeader>
        {/*
          `flex-col` here inherited CardFooter's `items-center`, which centred
          the pair on a phone. They are a primary action and its quieter
          alternative, so they read as a row from the left at every width.
        */}
        <CardFooter className="flex-row flex-wrap items-center justify-start gap-2 p-2.5 pt-0 sm:p-4 sm:pt-0">
          {hasPaid ? (
            <Button width="auto" size="sm" className="w-auto" onClick={() => onNavigate(PORTAL_PATHS.SEATS)}>
              Gerenciar equipe
            </Button>
          ) : (
            <>
              <Button width="auto" size="sm" className="w-auto" onClick={() => openPack('both')}>
                Montar plano Equipe
              </Button>
              <Button variant="ghost" width="auto" size="sm" className="w-auto" onClick={onUpgrade}>
                Ver preços
              </Button>
            </>
          )}
        </CardFooter>
      </Card>

      <PackUpgradeModal
        open={packFocus != null}
        focus={packFocus || 'both'}
        onClose={() => setPackFocus(null)}
        hasPaidAccess={hasPaid}
        current={sub}
        onCheckout={onCheckout}
        onUpdated={() => {
          invalidate();
          void refresh({ force: true });
        }}
      />
    </div>
  );
}
